import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema, OrganizationSchema, FAQPageSchema } from '@/components/feature/SeoSchema';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value) {
          params.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9rh4avnahb7382ovmg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        e.currentTarget.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  }, []);

  const testimonials = [
    {
      name: '墨染千秋',
      role: '签约画师',
      content: '琉璃神社为创作者提供了一个真正被尊重和理解的平台。这里的用户群体对内容有着极高的鉴赏力，每次发布作品都能收到大量有价值的反馈。',
    },
    {
      name: '林小夕',
      role: '琉璃·星会员',
      content: '从琉璃·月升级到琉璃·星，每一分钱都花得值。资源库的质量远超预期，线下活动的体验更是无与伦比。',
    },
    {
      name: '星辰大海',
      role: '社区活跃用户',
      content: '在这里找到了很多志同道合的朋友。琉璃神社不仅仅是一个内容平台，更像是一个温暖的家。',
    },
  ];

  const faqs = [
    {
      question: '琉璃神社是什么？',
      answer: '琉璃神社是一个综合性内容平台，为粉丝与创作者提供官方公告、正版资源下载、会员专属服务及社区互动功能。我们致力于打造一个权威、高品质的创作与交流生态。',
    },
    {
      question: '如何成为琉璃神社会员？',
      answer: '您可以在会员专区页面选择适合您的会员等级：琉璃·初（免费）、琉璃·月（月费）、琉璃·星（年费）或琉璃·光（终身）。选择后按提示完成支付即可立即享受对应权益。',
    },
    {
      question: '会员可以退款吗？',
      answer: '琉璃·月会员支持7天内无理由退款。琉璃·星和琉璃·光会员由于包含实体礼盒等定制内容，暂不支持退款，请在购买前仔细阅读会员权益说明。',
    },
    {
      question: '如何下载官方资源？',
      answer: '登录后前往资源下载页面，选择您需要的资源分类。免费用户可下载低分辨率预览版，付费会员可下载高清完整版。每个资源页面都有详细的下载说明与使用许可。',
    },
    {
      question: '如何联系琉璃神社客服？',
      answer: '您可以通过本页面的联系表单提交咨询，或发送邮件至 contact@liulishenshe.com。我们的在线客服工作时间为工作日 9:00-18:00（北京时间）。',
    },
  ];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <OrganizationSchema />
      <WebPageSchema
        title="关于我们 - 琉璃神社官方"
        description="了解琉璃神社的品牌故事、发展历程、口碑声誉及联系方式。琉璃神社致力于为粉丝与创作者提供权威信息平台。"
        url="https://liulishenshe.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://liulishenshe.com' },
          { name: '关于我们', url: 'https://liulishenshe.com/about' },
        ]}
      />
      <FAQPageSchema faqs={faqs} />

      <Navbar />

      <main>
        <div className="relative h-56 md:h-72 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Serene%20Japanese%20shrine%20with%20morning%20mist%2C%20stone%20pathway%20leading%20to%20traditional%20wooden%20building%2C%20ancient%20trees%2C%20soft%20golden%20sunrise%20light%2C%20peaceful%20and%20spiritual%20atmosphere%2C%20high%20detail%20cinematic&width=1600&height=600&seq=about-header&orientation=landscape')`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">关于我们</h1>
            <p className="text-white/70 text-sm">了解琉璃神社的故事与使命</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-10 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-accent-600 text-sm tracking-widest font-label uppercase">品牌故事</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2 mb-6">
                  始于热爱，忠于创作
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    琉璃神社成立于2018年，源于一群对ACG文化与艺术创作充满热情的年轻人。我们相信，每一份创作都值得被尊重，每一位创作者都值得拥有展示才华的舞台。
                  </p>
                  <p>
                    从最初的小型分享社区发展到今天拥有超过120万注册用户的综合性内容平台，琉璃神社始终秉持"以创作者为中心，以用户需求为导向"的核心理念。我们不仅提供优质的正版资源，更致力于构建一个健康、活跃、富有创造力的社区生态。
                  </p>
                  <p>
                    2026年，琉璃神社迎来了全新的发展阶段。「幻境回廊」创意工坊的推出标志着我们从内容消费平台向创作者经济平台的跨越，而「星月流光」会员体系的全面升级则让每一位支持者都能享受到更加贴心和丰富的专属服务。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-80 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20traditional%20art%20studio%20with%20warm%20lighting%2C%20wooden%20shelves%20filled%20with%20art%20books%2C%20ink%20paintings%20on%20walls%2C%20tatami%20floor%2C%20creative%20workspace%20atmosphere%2C%20soft%20diffused%20light%20from%20paper%20lanterns%2C%20elegant%20and%20inspiring&width=800&height=600&seq=about-brand&orientation=portrait"
                  alt="琉璃神社工作室"
                  title="琉璃神社官方工作室"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="py-10 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-accent-600 text-sm tracking-widest font-label uppercase">口碑声誉</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2">
                  来自社区的声音
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.name}
                    className="bg-background-50 rounded-lg p-6 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                        <span className="text-accent-600 font-heading font-bold text-sm">
                          {t.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-foreground-950 text-sm">{t.name}</div>
                        <div className="text-foreground-500 text-xs">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <span className="text-accent-600 text-sm tracking-widest font-label uppercase">联系我们</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2 mb-6">
                  期待与你交流
                </h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-8">
                  如果你有任何问题、建议或合作意向，欢迎通过以下方式联系琉璃神社团队。我们会在1-2个工作日内回复你的消息。
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">电子邮箱</div>
                      <p className="text-foreground-500 text-sm">contact@liulishenshe.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-customer-service-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">在线客服</div>
                      <p className="text-foreground-500 text-sm">工作日 9:00 - 18:00（北京时间）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">办公地址</div>
                      <p className="text-foreground-500 text-sm">上海市浦东新区张江高科技园区</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-lg mb-6">
                    发送消息
                  </h3>

                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none text-sm transition-colors"
                          placeholder="请输入你的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none text-sm transition-colors"
                          placeholder="请输入你的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-subject"
                          name="subject"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none text-sm transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={5}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 focus:border-primary-300 focus:ring-1 focus:ring-primary-200 outline-none text-sm transition-colors resize-none"
                          placeholder="请输入你想说的话（最多500字）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                    </div>

                    {formStatus === 'success' && (
                      <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                        <i className="ri-check-line mr-1.5"></i>
                        消息已发送成功！我们会在1-2个工作日内回复你。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                        <i className="ri-error-warning-line mr-1.5"></i>
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="mt-6 w-full px-6 py-3 rounded-lg bg-primary-500 text-white font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap text-sm"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="text-accent-600 text-sm tracking-widest font-label uppercase">常见问题</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-2">
                  你可能想了解
                </h2>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer select-none">
                      <h4 className="font-semibold text-foreground-950 text-sm pr-4">{faq.question}</h4>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}